import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { AlertCircle, ChevronLeft, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  CoverImageDrop,
  type CoverImageResult,
} from "@/components/CoverImageDrop"
import {
  useCreateCategory,
  useGetCategory,
  useUpdateCategory,
} from "@/lib/queries/categories"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Category form (combined New + Edit)
// LEGACY: apps/admin/src/pages/AddCategory.jsx
// API:    POST /admin/category_get        { id }                 (when editing)
//         POST /admin/add_category        { name, cover_image, cover_image_name, order }   (create)
//         POST /admin/update_category     { id, name, cover_image, cover_image_name, order } (update)
//
// Note on legacy URL casing: legacy posts to "/admin/Update_category" (capital U).
// Express's default routing is case-INSENSITIVE so it works against the
// "/update_category" route. We use lowercase to match the actual route
// definition; behavior is identical.
//
// ─── Field parity (legacy ↔ v2) ──────────────────────────────────────
//   name             ← Category Name input
//   cover_image      ← s3response object on new upload; OMITTED when
//                      editing without picking a new file (backend keeps
//                      existing). Legacy sends the URL string in that case;
//                      backend tolerates both. Omitting is cleaner and
//                      matches the album/chapter pattern.
//   cover_image_name ← file_name from upload (only sent when new file)
//   order            ← number input
//
// Validation parity:
//   - name required (legacy: !name → toast)
//   - cover_image required on CREATE only (legacy: !S3_cover → toast; on
//     edit S3_cover is pre-filled from the existing URL so the check passes)
// ───────────────────────────────────────────────────────────────────────

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  order: z.coerce.number().int().min(0),
  // V-Q10: admin soft-hide. True = hidden from every user-facing
  // categories rail; still visible in admin lists so ops can un-hide.
  is_hidden: z.boolean(),
})

type FormValues = z.infer<typeof schema>

const DEFAULT_VALUES: FormValues = { name: "", order: 0, is_hidden: false }

export default function CategoryForm() {
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id
  const navigate = useNavigate()

  const categoryQ = useGetCategory(id)
  const createMut = useCreateCategory()
  const updateMut = useUpdateCategory()
  const submittingMut = isEdit ? updateMut : createMut

  const [coverResult, setCoverResult] = useState<CoverImageResult | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_VALUES,
  })

  useEffect(() => {
    if (!isEdit || !categoryQ.data) return
    reset({
      name: categoryQ.data.name ?? "",
      order: typeof categoryQ.data.order === "number" ? categoryQ.data.order : 0,
      is_hidden: !!categoryQ.data.is_hidden,
    })
  }, [isEdit, categoryQ.data, reset])

  const existingCoverPreview = isEdit ? categoryQ.data?.cover_image : undefined

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null)

    if (!isEdit && !coverResult) {
      setSubmitError("Please upload a cover image.")
      return
    }

    const payload: Parameters<typeof createMut.mutateAsync>[0] = {
      ...(isEdit && id ? { id } : {}),
      name: values.name,
      order: values.order,
      is_hidden: values.is_hidden,
    }
    if (coverResult?.s3response) {
      payload.cover_image = coverResult.s3response
      payload.cover_image_name = coverResult.file_name
    }

    try {
      const res = await submittingMut.mutateAsync(payload)
      if (!res?.success) {
        setSubmitError(
          res?.message || `Failed to ${isEdit ? "update" : "create"} category.`
        )
        return
      }
      navigate("/categories")
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : `Failed to ${isEdit ? "update" : "create"} category.`
      )
    }
  })

  const isLoadingPrefill = isEdit && categoryQ.isLoading

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <a href="/categories">
            <ChevronLeft className="mr-1 h-4 w-4" /> Back to categories
          </a>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{isEdit ? "Edit category" : "Create category"}</CardTitle>
          <CardDescription>
            {isEdit
              ? categoryQ.data?.name || "Loading…"
              : "Cover image is required."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isEdit && categoryQ.isError && (
            <div
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Failed to load this category. Try refreshing.</span>
            </div>
          )}

          {isLoadingPrefill ? (
            <div className="grid h-32 place-items-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <form className="space-y-6" onSubmit={onSubmit} noValidate>
              <div className="space-y-2">
                <Label htmlFor="name">
                  Category name<span className="text-destructive"> *</span>
                </Label>
                <Input
                  id="name"
                  aria-invalid={!!errors.name}
                  {...register("name")}
                />
                {errors.name && (
                  <p className="text-xs text-destructive">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>
                  Cover image
                  {!isEdit && <span className="text-destructive"> *</span>}
                </Label>
                <CoverImageDrop
                  initialPreview={existingCoverPreview}
                  result={coverResult}
                  onResult={setCoverResult}
                  onUploadingChange={setIsUploading}
                  disabled={submittingMut.isPending}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="order">Order</Label>
                <Input
                  id="order"
                  type="number"
                  min={0}
                  className="max-w-xs"
                  {...register("order")}
                />
                {errors.order && (
                  <p className="text-xs text-destructive">
                    {errors.order.message}
                  </p>
                )}
              </div>

              {/* V-Q10: admin soft-hide toggle. Hidden categories drop
                  out of the user-facing /v2/discover/categories rail
                  but stay in admin lists. Reversible. */}
              <div className="flex items-center justify-between rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="is_hidden">Hide from users</Label>
                  <p className="text-xs text-muted-foreground">
                    Keeps this category out of the app while
                    work-in-progress. Toggle off to publish back.
                  </p>
                </div>
                <Controller
                  control={control}
                  name="is_hidden"
                  render={({ field }) => (
                    <Switch
                      id="is_hidden"
                      checked={!!field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>

              {submitError && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{submitError}</span>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="submit"
                  disabled={isUploading || submittingMut.isPending}
                >
                  {(submittingMut.isPending || isUploading) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isUploading
                    ? "Uploading…"
                    : submittingMut.isPending
                      ? isEdit
                        ? "Saving…"
                        : "Creating…"
                      : isEdit
                        ? "Update category"
                        : "Create category"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/categories")}
                  disabled={submittingMut.isPending}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
